import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonService } from './common.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormConfig } from '../models/dynamic.model';
import { ValidatorPattern } from '../utility/validators.pattern';

@Injectable({
  providedIn: 'root',
})
export class FormControlValidatorsService {
  constructor(
    private readonly commonService: CommonService,
    private readonly fb: FormBuilder,
  ) {}

  public generateForm(
    jsonConfig: any,
    destroyRef: DestroyRef,
  ): {
    form: FormGroup;
    errorMessages: any;
    options: any;
  } {
    const form = this.createForm(jsonConfig);
    const errorMessages = this.setErrorMessageObject(jsonConfig);
    const options = this.getSelectOptions(jsonConfig);
    this.handleRestriction(form, jsonConfig, destroyRef);
    this.noInitialWhiteSpace(form, jsonConfig, destroyRef);
    return { form, errorMessages, options };
  }

  public createForm(formConfig: FormConfig): FormGroup {
    const formGroup: any = {};
    for (const field of formConfig.fields) {
      const validators = [];
      if (field.required) {
        validators.push(Validators.required);
      }
      if (field.minValue) {
        validators.push(Validators.minLength(field.minValue));
      }
      if (field.pattern) {
        validators.push(Validators.pattern(field.pattern));
      }
      if (field.maxLength) {
        validators.push(Validators.maxLength(field.maxLength));
      }
      formGroup[field.id] = [
        field.isDisableField ? { value: '', disabled: true } : '',
        validators,
      ];
    }
    const group = this.fb.group(formGroup);
    return group;
  }

  public getErrorMessage(
    form: FormGroup,
    errorMessages: any,
    fieldId: string,
  ): string {
    const control = form.get(fieldId);
    const fieldErrorMessages = errorMessages[fieldId];
    if (control?.errors && fieldErrorMessages) {
      const errorKey = Object.keys(control.errors)[0];
      if (errorKey === 'dateOrderInvalid') {
        return 'Please ensure the End Date is greater than the Start Date';
      }
      return fieldErrorMessages[errorKey] ?? '';
    }
    return '';
  }

  private subscribeValueChanges(
    control: AbstractControl,
    destroyRef: DestroyRef,
    handler: (value: unknown) => void,
  ): void {
    control.valueChanges
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(handler);
  }

  private setErrorMessageObject(formConfig: any): any {
    const errorMessages: any = {};
    for (const field of formConfig.fields) {
      if (
        field.requiredError ||
        field.patternError ||
        field.minError ||
        field.emailPatternError ||
        field.patternError ||
        field.phonePatternError ||
        field.maxLengthError ||
        ''
      ) {
        errorMessages[field.id] = {
          required: field.requiredError || '',
          pattern:
            field.emailPatternError ||
            field.patternError ||
            field.phonePatternError ||
            '',
          minlength: field.minError || '',
        };
      }
    }
    return errorMessages;
  }

  private getSelectOptions(jsonConfig: any): {
    selectOptions: any[];
    radioOptions: any[];
  } {
    const selectField = jsonConfig.fields.find(
      (field: any) => field.type === 'select',
    );
    const radioField = jsonConfig.fields.find(
      (field: any) => field.type === 'radio',
    );
    const selectOptions = selectField ? selectField.options : [];
    const radioOptions = radioField ? radioField.options : [];
    return { selectOptions, radioOptions };
  }

  private handleRestriction(
    form: FormGroup,
    formConfig: any,
    destroyRef: DestroyRef,
  ) {
    for (const field of formConfig.fields) {
      if (field.allowNumeric) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (
              typeof value === 'string' &&
              this.commonService.isValidField(value)
            ) {
              const sanitizedValue = value.replace(/\D/g, '');
              const trimmedValue = sanitizedValue.slice(0, field.maxValue);
              if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      } else if (field.allowAlphabet) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (
              typeof value === 'string' &&
              this.commonService.isValidField(value)
            ) {
              const sanitizedValue = value.replace(/[^a-zA-Z]/gi, '');
              const trimmedValue = sanitizedValue.slice(0, field.maxValue);
              if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      } else if (field.allowAlphaNumeric) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (
              typeof value === 'string' &&
              this.commonService.isValidField(value)
            ) {
              const sanitizedValue = value.replace(/[^a-zA-Z0-9]/gi, '');
              const trimmedValue = sanitizedValue.slice(0, field.maxValue);
              if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      } else if (field.allowAlphabetWithSpace) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (
              typeof value === 'string' &&
              this.commonService.isValidField(value)
            ) {
              if (value !== ' ') {
                const sanitizedValue = value.replace(/[^a-zA-Z ]/gi, '');
                const trimmedValue = sanitizedValue.slice(0, field.maxValue);
                if (
                  value !== sanitizedValue ||
                  sanitizedValue !== trimmedValue
                ) {
                  control.setValue(trimmedValue, { emitEvent: false });
                }
              } else {
                const trimmedValue = value.trimStart();
                if (value !== trimmedValue) {
                  control.setValue(trimmedValue, { emitEvent: false });
                }
              }
            }
          });
        }
      } else if (
        field.allowAlphaNumericWithDotUnderscoreDashSpecialCharacters
      ) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (
              typeof value === 'string' &&
              this.commonService.isValidField(value)
            ) {
              const sanitizedValue = value.replace(/[^a-z0-9._-]/gi, '');
              const trimmedValue = sanitizedValue.slice(0, field.maxValue);
              if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      } else if (field.allowAlphabetWithHyphenSpace) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (typeof value !== 'string') {
              return;
            }
            if (this.commonService.isValidField(value)) {
              const sanitizedValue = value.replace(/[^a-zA-Z -]/g, '');
              const trimmedValue = sanitizedValue.slice(0, field.maxValue);
              if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      } else if (field.allowAlphaNumericWithSpaceNoSpecialCharacter) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (
              typeof value === 'string' &&
              this.commonService.isValidField(value)
            ) {
              const sanitizedValue = value.replace(
                ValidatorPattern.alphanumericWithSpace,
                '',
              );
              const trimmedValue = sanitizedValue.slice(0, field.maxValue);
              if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      } else if (form.get(field.id) && field.isCheckForMaxValue) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (value !== ' ') {
              const trimmedValue = ((value as string) || '').slice(0, field.maxValue);
              if (value !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      } else if (field.allowNumberWithFirstNonZeroNumber) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (
              typeof value === 'string' &&
              this.commonService.isValidField(value)
            ) {
              let sanitizedValue = value.replace(/\D/g, '');
              if (sanitizedValue.startsWith('0')) {
                sanitizedValue = sanitizedValue.replace(/^0+/, '');
              }
              const trimmedValue = sanitizedValue.slice(0, field.maxValue);
              if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      }
    }
  }

  private noInitialWhiteSpace(
    form: FormGroup,
    formConfig: any,
    destroyRef: DestroyRef,
  ) {
    for (const field of formConfig.fields) {
      if (field.noInitialWhiteSpace) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeValueChanges(control, destroyRef, (value) => {
            if (typeof value === 'string') {
              const trimmedValue = value.trimStart();
              if (value !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      }
    }
  }
}
